import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Skeleton,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { FetchBanner } from '../../../services/api/category';

const MultiItemCarousel = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  const hasFetched = useRef(false);
  const autoPlayRef = useRef(null);

  // Determine items per view based on screen size (like Jio Mart)
  const getItemsPerView = () => {
    if (isMobile) return 1.2; // Shows 1.2 items, giving preview of next
    if (isTablet) return 2.2; // Shows 2.2 items
    return 3.2; // Shows 3.2 items on desktop (shows part of next item)
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());
  const totalSlides = Math.ceil(banners.length / Math.floor(itemsPerView));

  // Update on resize
  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, isTablet]);

  // Fetch banners - only once
  useEffect(() => {
    const fetchBanners = async () => {
      if (hasFetched.current) return;
      hasFetched.current = true;
      
      try {
        const response = await FetchBanner();
        const data = response?.data || response;
        
        if (data?.success && data?.data?.[0]?.mediaAssets) {
          setBanners(data.data[0].mediaAssets);
        } else if (Array.isArray(data)) {
          setBanners(data);
        } else {
          setError('Invalid banner data format');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching banners:', err);
        setError('Failed to load banners');
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Auto-scroll functionality
  useEffect(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }

    if (isAutoPlaying && totalSlides > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= totalSlides - 1) {
            return 0; // Loop back to start
          }
          return prev + 1;
        });
      }, 3000); // Change slide every 3 seconds (adjust as needed)
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, totalSlides]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(totalSlides - 1, prev + 1));
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  // Calculate translateX value
  const getTranslateX = () => {
    const itemWidth = 100 / itemsPerView;
    return -currentIndex * itemWidth;
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Stack direction="row" spacing={2}>
          {[1, 2, 3].map((_, idx) => (
            <Skeleton 
              key={idx}
              variant="rectangular" 
              width={`${100 / itemsPerView}%`}
              height={isMobile ? 200 : isTablet ? 300 : 400}
              animation="wave"
              sx={{ borderRadius: 2, flexShrink: 0 }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (error || banners.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {error || 'No banners available'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden', px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Carousel Container */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            transition: 'transform 0.4s ease-in-out',
            transform: `translateX(${getTranslateX()}%)`,
          }}
        >
          {banners.map((banner, index) => (
            <Box
              key={banner.id || index}
              sx={{
                flex: `0 0 ${100 / itemsPerView}%`,
                px: 1,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingBottom: '56.25%', // 16:9 aspect ratio
                  marginTop: 2,
                  marginRight: 1,
                  overflow: 'hidden',
                  borderRadius: 2,
                  boxShadow: theme.shadows[2],
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <img
                  src={banner.url}
                  alt={banner.altText || `Banner ${index + 1}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 8,
                  }}
                  loading="lazy"
                />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Navigation Arrows */}
        {currentIndex > 0 && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'white' },
              zIndex: 2,
              boxShadow: theme.shadows[2],
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        {currentIndex < totalSlides - 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 40,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'white' },
              zIndex: 2,
              boxShadow: theme.shadows[2],
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}

        {/* Auto-play Toggle Button */}
        {/* <IconButton
          onClick={toggleAutoPlay}
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            zIndex: 2,
            size: 'small',
          }}
        >
          {isAutoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton> */}
      </Box>

      {/* Dot Indicators */}
      {totalSlides > 1 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'center',
            mt: 2,
          }}
        >
          {[...Array(totalSlides)].map((_, index) => (
            <Box
              key={index}
              onClick={() => handleDotClick(index)}
              sx={{
                width: currentIndex === index ? 24 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: currentIndex === index ? theme.palette.primary.main : 'rgba(0,0,0,0.2)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default MultiItemCarousel;